import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';
import { buildSignBroadcast } from '@/utils/kleverTx';
import {
  setProfiles,
  appendProfiles,
  setUserBalance,
  setWalletAddress,
  setPagination,
  setError,
  setLoading,
  QuickFilter,
  SortOption,
  LocationScope,
  PaginationState,
} from '../slices/marketplaceSlice';
import { ProfileListing } from '@/app/marketplace/components/ProfileCard';

interface FetchProfilesParams {
  filter?: QuickFilter;
  sort?: SortOption;
  scope?: LocationScope;
  page?: number;
  limit?: number;
  append?: boolean; // If true, append to existing profiles instead of replacing
}

interface FetchProfilesResponse {
  profiles: ProfileListing[];
  pagination: PaginationState;
  /** Server's effective scope after fallback (downgrade when home/touring missing). */
  scope?: LocationScope;
  /** What the caller actually asked for, for surfacing downgrade hints. */
  scopeRequested?: LocationScope;
  /** Scopes the caller can use — drives which scope buttons render. */
  availableScopes?: LocationScope[];
  /** Country + state being filtered on, so UI can label what's being shown. */
  scopeArea?: { countryCode: string; admin1: string | null } | null;
}

interface UserBalanceResponse {
  balance: number;
  walletAddress?: string;
}

/**
 * Fetch marketplace profiles with filtering, sorting, and pagination
 */
export const fetchMarketplaceProfiles = createAsyncThunk(
  'marketplace/fetchProfiles',
  async (params: FetchProfilesParams, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get<FetchProfilesResponse>('/marketplace/profiles', {
        params: {
          filter: params.filter || 'all',
          sort: params.sort || 'highest-value',
          scope: params.scope || 'global',
          page: params.page || 1,
          limit: params.limit || 20,
        },
      });

      // Either append to existing profiles or replace them
      if (params.append) {
        dispatch(appendProfiles(response.data.profiles));
      } else {
        dispatch(setProfiles(response.data.profiles));
      }

      dispatch(setPagination(response.data.pagination));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to load marketplace profiles';
      dispatch(setError(message));
      return rejectWithValue(message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

/**
 * Fetch user's token balance (requires authentication)
 * For wallet users: fetches from connected wallet
 * For email users: fetches from backend custodial wallet
 */
export const fetchUserBalance = createAsyncThunk(
  'marketplace/fetchBalance',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Backend now handles both wallet and email users
      console.debug('[Balance] Fetching user balance from backend...');
      const response = await api.get<UserBalanceResponse>('/marketplace/balance');

      //console.debug('[Balance] Received balance:', response.data.balance, 'KLYX' );
      //console.debug('[Balance] Wallet address:', response.data.walletAddress );

      dispatch(setUserBalance(response.data.balance));
      if (response.data.walletAddress) {
        dispatch(setWalletAddress(response.data.walletAddress));
      }
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Failed to load user balance';
      // Don't set error for balance fetch - it's not critical
      console.error('[Balance] Error:', message);
      return rejectWithValue(message);
    }
  }
);

/**
 * Purchase a profile
 */
export const purchaseProfile = createAsyncThunk(
  'marketplace/purchaseProfile',
  async ({ profileId, customPrice }: { profileId: string; customPrice: number }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post<any>('/marketplace/purchase', { profileId, customPrice });

      // Check if this requires wallet signing (for connected wallet users)
      if (response.data.requiresSignature) {
        const { payload, metadata, network, kdaFee } = response.data;
        const txHash = await buildSignBroadcast({ payload, metadata, network, kdaFee });

        // Call the confirmation endpoint
        console.log('[Purchase] Confirming purchase...');
        const confirmResponse = await api.post('/marketplace/purchase/confirm', {
          profileId,
          txHash,
          customPrice
        });

        console.log('✅ Purchase confirmed:', confirmResponse.data);

        // After successful purchase, refresh balance and profiles
        await dispatch(fetchUserBalance());
        await dispatch(fetchMarketplaceProfiles({ filter: 'all', sort: 'highest-value', page: 1 }));

        return confirmResponse.data;
      } else {
        // Email user - backend signed and broadcast the transaction
        // After successful purchase, refresh balance and profiles
        await dispatch(fetchUserBalance());
        await dispatch(fetchMarketplaceProfiles({ filter: 'all', sort: 'highest-value', page: 1 }));

        return response.data;
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Failed to purchase profile';
      dispatch(setError(message));
      return rejectWithValue(message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

/**
 * Prepare profile spotlight transaction
 */
/** Marketplace spotlight endpoints accept an optional `audienceScope`
 *  — 'location' (default; existing marketplace behavior — targets
 *  the profile owner's state) or 'global' (surfaces on the
 *  /agents/discover Spotlighted rail for every viewer, no location
 *  filter). Threading it through prepare/execute/record so both
 *  wallet + email flows can buy either type. */
type AudienceScope = 'location' | 'global';

export const prepareProfileSpotlight = createAsyncThunk(
  'marketplace/prepareProfileSpotlight',
  async (
    { profileId, durationHours, audienceScope }: { profileId: string; durationHours: number; audienceScope?: AudienceScope },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post('/marketplace/spotlight/prepare', {
        profileId,
        durationHours,
        ...(audienceScope ? { audienceScope } : {}),
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to prepare spotlight';
      return rejectWithValue(message);
    }
  }
);

/**
 * Execute profile spotlight for email users (backend signs transaction)
 */
export const executeProfileSpotlightEmailUser = createAsyncThunk(
  'marketplace/executeProfileSpotlightEmailUser',
  async (
    { profileId, durationHours, audienceScope }: { profileId: string; durationHours: number; audienceScope?: AudienceScope },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post('/marketplace/spotlight/execute', {
        profileId,
        durationHours,
        ...(audienceScope ? { audienceScope } : {}),
      });
      return response.data;
    } catch (error: any) {
      // Check if error response contains pricing data (insufficient funds case)
      if (error.response?.data?.pricing && error.response?.data?.balance) {
        // Return the full error response data including pricing
        return rejectWithValue(error.response.data);
      }
      const message = error.response?.data?.error || 'Failed to execute spotlight';
      return rejectWithValue(message);
    }
  }
);

/**
 * Record profile spotlight purchase in database
 */
export const recordProfileSpotlight = createAsyncThunk(
  'marketplace/recordProfileSpotlight',
  async (
    { profileId, durationHours, txHash, audienceScope }: { profileId: string; durationHours: number; txHash: string; audienceScope?: AudienceScope },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post('/marketplace/spotlight/record', {
        profileId,
        durationHours,
        txHash,
        ...(audienceScope ? { audienceScope } : {}),
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to record spotlight';
      return rejectWithValue(message);
    }
  }
);
